import { Controller } from "stimulus";
import Rails from "@rails/ujs";
import debounce from "lodash/debounce";

export default class extends Controller {
  static targets = [
    "page",
    "card",
    "form",
    "correctRadioButton",
    "correctCheckbox",
    "correctInputError",
    "passingScoreInput",
    "passingScoreRow",
    "allQuestionsRequiredToggle",
    "settingAccordion"
  ];

  static values = {
    unsavedTitle: String,
    unsavedMessage: String,
    unsavedMessageNewPage: String,
    unsavedCancel: String,
    unsavedAllow: String,
    unsavedAllowNewPage: String,
    passingScoreUrl: String,
    settingsUrl: String
  };

  connect() {
    this._boundPageHandler = this._switchPageFromEvent.bind(this);
    this._boundRadioChanged = this._onRadioChanged.bind(this);
    this._boundCheckboxChanged = this._onCheckboxChanged.bind(this);
    this._boundCorrectAlternativeChanged = this._syncCorrectAnswer.bind(this);
    this._boundFormInvalid = this._scrollToFirstError.bind(this);
    this._debouncedSettingSaves = {};

    this.element.addEventListener('promote:form-invalid', this._boundFormInvalid);

    document.addEventListener('promote:questionnaire-builder:switch-page', this._boundPageHandler);
    document.addEventListener(
      'questionnaires--content-tab:correctAlternativeChanged',
      this._boundCorrectAlternativeChanged
    );

    if (this.hasCorrectRadioButtonTarget) {
      this.correctRadioButtonTargets.forEach(radio => {
        radio.addEventListener('change', this._boundRadioChanged);
      });
    }

    if (this.hasCorrectCheckboxTarget) {
      this.correctCheckboxTargets.forEach(checkbox => {
        checkbox.addEventListener('change', this._boundCheckboxChanged);
      });
    }
  }

  disconnect() {
    this.element.removeEventListener('promote:form-invalid', this._boundFormInvalid);

    document.removeEventListener(
      'promote:questionnaire-builder:switch-page',
      this._boundPageHandler
    );

    document.removeEventListener(
      'questionnaires--content-tab:correctAlternativeChanged',
      this._boundCorrectAlternativeChanged
    );

    if (this.hasCorrectRadioButtonTarget) {
      this.correctRadioButtonTargets.forEach(radio => {
        radio.removeEventListener('change', this._boundRadioChanged);
      });
    }

    if (this.hasCorrectCheckboxTarget) {
      this.correctCheckboxTargets.forEach(checkbox => {
        checkbox.removeEventListener('change', this._boundCheckboxChanged);
      });
    }
  }

  correctRadioButtonTargetConnected(target) {
    target.removeEventListener('change', this._boundRadioChanged);
    target.addEventListener('change', this._boundRadioChanged);
  }

  correctRadioButtonTargetDisconnected(target) {
    target.removeEventListener('change', this._boundRadioChanged);
  }

  correctCheckboxTargetConnected(target) {
    target.removeEventListener('change', this._boundCheckboxChanged);
    target.addEventListener('change', this._boundCheckboxChanged);
  }

  correctCheckboxTargetDisconnected(target) {
    target.removeEventListener('change', this._boundCheckboxChanged);
  }

  async addPage(event) {
    event.preventDefault();

    const url = event.currentTarget.dataset.addPageUrl;

    const unloadChecker = this.application
      .getControllerForElementAndIdentifier(document.body, 'unload-checker');

    let hasPending = false;
    const formIsInvalid = this.hasFormTarget && !this.formTarget.checkValidity();

    if (unloadChecker) {
      hasPending = await unloadChecker.hasPendingForms();
    }

    if (hasPending || formIsInvalid) {
      try {
        await new Promise((resolve, reject) => {
          Rails.confirm(null, this.element, {
            confirmTitle: this.unsavedTitleValue,
            confirmMessage: this.unsavedMessageNewPageValue,
            confirmCancel: this.unsavedCancelValue,
            confirmAllow: this.unsavedAllowNewPageValue
          }, { confirmCallback: resolve, cancelCallback: reject });
        });
      } catch {
        return;
      }

      if (this.hasFormTarget) {
        const saved = await this.saveCurrentForm();
        if (!saved) return;
      }
    }

    Rails.ajax({
      type: 'POST',
      url,
      dataType: 'script'
    });
  }

  async switchPage(event) {
    if (event.target.closest('button, a, input, [role="button"]')) return;

    const url = event.currentTarget.dataset.url;

    const unloadChecker = this.application
      .getControllerForElementAndIdentifier(document.body, 'unload-checker');

    let hasPending = false;
    const formIsInvalid = this.hasFormTarget && !this.formTarget.checkValidity();

    if (unloadChecker) {
      hasPending = await unloadChecker.hasPendingForms();
    }

    if (hasPending || formIsInvalid) {
      try {
        await new Promise((resolve, reject) => {
          Rails.confirm(null, this.element, {
            confirmTitle: this.unsavedTitleValue,
            confirmMessage: this.unsavedMessageValue,
            confirmCancel: this.unsavedCancelValue,
            confirmAllow: this.unsavedAllowValue
          }, { confirmCallback: resolve, cancelCallback: reject });
        });
      } catch {
        return;
      }

      if (this.hasFormTarget) {
        const saved = await this.saveCurrentForm();
        if (!saved) return;
      }
    }

    Rails.ajax({
      type: 'GET',
      url,
      dataType: 'script',
      error: () => {
        this.element.dispatchEvent(new CustomEvent('ajax:error', { bubbles: true }));
      }
    });
  }

  saveCurrentForm() {
    // This path validates directly instead of submitting, so neither the submit
    // handler nor the form-errors event fires: scroll from here too
    if (!this._validate()) {
      this._scrollToFirstError();

      return Promise.resolve(false);
    }

    if (this.hasFormTarget && !this.formTarget.checkValidity()) {
      const formErrorsController = this.application.controllers.find(
        c => c.identifier === 'form-errors' && c.element === this.formTarget
      );

      formErrorsController?.validateForm();
      this._scrollToFirstError();

      return Promise.resolve(false);
    }

    return new Promise((resolve) => {
      const form = this.formTarget;
      let saved = true;

      const handleError = () => { saved = false; };

      document.addEventListener(
        'promote:display-error-message',
        handleError,
        { once: true }
      );

      Rails.ajax({
        url: form.action,
        type: (form.dataset.method || 'patch').toUpperCase(),
        data: new FormData(form),
        dataType: 'script',
        success: () => {
          document.removeEventListener('promote:display-error-message', handleError);
          resolve(saved);
        },
        error: () => {
          document.removeEventListener('promote:display-error-message', handleError);
          resolve(false);
        }
      });
    });
  }

  async deletePage(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const row = button.closest('[data-questionnaires--content-tab-target="page"]');

    // Case 3: already on this page — show confirmation and delete
    if (row.classList.contains('active')) {
      Rails.confirm(null, button, this._deleteConfirmData(button), {
        confirmCallback: () => {
          Rails.ajax({
            type: 'DELETE',
            url: button.dataset.deleteUrl,
            dataType: 'script',
            error: () => {
              this.element.dispatchEvent(new CustomEvent('ajax:error', { bubbles: true }));
            }
          });
        }
      });
      return;
    }

    const unloadChecker = this.application
      .getControllerForElementAndIdentifier(document.body, 'unload-checker');
    const hasPending = unloadChecker ? await unloadChecker.hasPendingForms() : false;

    if (!hasPending) {
      // Case 2: no unsaved changes — show delete confirmation immediately
      const activeRow = this.pageTargets.find(p => p.classList.contains('active'));
      const activePageId = activeRow
        ? activeRow.id.replace('questionnaires-builder-vat-item-', '')
        : null;

      Rails.confirm(null, button, this._deleteConfirmData(button), {
        confirmCallback: () => {
          Rails.ajax({
            type: 'DELETE',
            url: button.dataset.deleteUrl,
            data: activePageId ? `selected_page_id=${activePageId}` : '',
            dataType: 'script',
            error: () => {
              this.element.dispatchEvent(new CustomEvent('ajax:error', { bubbles: true }));
            }
          });
        }
      });
      return;
    }

    // Case 1: unsaved changes — warn first, then switch to target page on confirm
    try {
      await unloadChecker.verify({
        confirmTitle: this.unsavedTitleValue,
        confirmMessage: this.unsavedMessageValue,
        confirmCancel: this.unsavedCancelValue,
        confirmAllow: this.unsavedAllowValue
      });
    } catch {
      return;
    }

    Rails.ajax({
      type: 'GET',
      url: row.dataset.url,
      dataType: 'script',
      error: () => {
        this.element.dispatchEvent(new CustomEvent('ajax:error', { bubbles: true }));
      }
    });
  }

  // Reads the field rather than the stored value so an unsaved edit still gets
  // the right warning.
  _deleteConfirmData(button) {
    const data = { ...button.dataset };
    const removed = parseInt(button.dataset.partsRemoved, 10);

    if (!this.hasPassingScoreRowTarget || !this.hasPassingScoreInputTarget || !removed) {
      return data;
    }

    const row = this.passingScoreRowTarget;
    const passingScore = parseInt(this.passingScoreInputTarget.value, 10);
    const maxScore = parseInt(row.dataset.maxScore, 10);

    if (passingScore > maxScore - removed) {
      data.confirmMessage = `${data.confirmMessage} ${row.dataset.loweredWarning}`;
      data.confirmCheck = 'true';
      data.confirmCheckMessage = row.dataset.loweredCheck;
    }

    return data;
  }

  savePassingScore(event) {
    // NumberComponent's stepper/arrow-key handling dispatches its own blur event, and only a
    // genuine blur event from browser should trigger a save.
    if (!event.isTrusted) {
      return;
    }

    const input = event.target;
    const value = input.value;

    // A cleared field resets to the stored score anyway, so put it back here rather than
    // letting the server refuse it.
    if (value === '') {
      input.value = input.defaultValue;
      return;
    }

    if (value === input.defaultValue) {
      return;
    }

    input.defaultValue = value;

    Rails.ajax({
      type: 'PATCH',
      url: this.passingScoreUrlValue,
      data: `passing_score=${encodeURIComponent(value)}`,
      dataType: 'script'
    });
  }

  // Each field gets its own debounce, so typing in one field never drops the
  // pending save of another.
  saveSettingDebounced(event) {
    const input = event.target;

    this._debouncedSettingSaves[input.name] ||= debounce(this._saveSetting.bind(this), 1000);
    this._debouncedSettingSaves[input.name](input);
  }

  // Leaving the field saves straight away, so nothing typed is lost when the
  // user moves on before the debounce runs out.
  flushSetting(event) {
    this._debouncedSettingSaves[event.target.name]?.flush();
  }

  blurSetting(event) {
    event.preventDefault();
    event.target.blur();
  }

  saveSetting(event) {
    this._saveSetting(event.target);
  }

  _saveSetting(input) {
    const value = input.type === 'checkbox' ? input.checked : input.value;

    if (input.type !== 'checkbox') {
      if (value === input.defaultValue) return;

      input.defaultValue = value;
    }

    Rails.ajax({
      type: 'PATCH',
      url: this.settingsUrlValue,
      data: `${encodeURIComponent(input.name)}=${encodeURIComponent(value)}`,
      dataType: 'script'
    });
  }

  // The passing score row is rendered again after every change that affects it,
  // so a warning coming from one of those changes opens the behavior section
  // where the field lives. The row from the page load leaves it collapsed.
  passingScoreRowTargetConnected(target) {
    if (!this._passingScoreRowRendered) {
      this._passingScoreRowRendered = true;
      return;
    }

    if (target.dataset.messageType !== 'warning' || !this.hasSettingAccordionTarget) return;

    const accordion = this.application.getControllerForElementAndIdentifier(
      this.settingAccordionTarget,
      'components--accordion'
    );
    const content = this.settingAccordionTarget.querySelector('.accordion-content');

    if (accordion && content?.classList.contains('collapsed')) accordion.expand();
  }

  addQuestionPart(event) {
    event.preventDefault();

    const type = event.currentTarget.dataset.questionPartType;
    const url  = event.currentTarget.dataset.questionPartsUrl;

    const allRequired = this.hasAllQuestionsRequiredToggleTarget &&
      this.allQuestionsRequiredToggleTarget.checked;

    Rails.ajax({
      type: 'POST',
      url,
      data: `question_part[type]=${encodeURIComponent(type)}` +
        `&question_part[mandatory]=${allRequired}`,
      dataType: 'script'
    });
  }

  addAlternative(event) {
    event.preventDefault();

    const url = event.currentTarget.dataset.alternativesUrl;

    Rails.ajax({
      type: 'POST',
      url,
      dataType: 'script'
    });
  }

  deleteQuestionPart(event) {
    event.preventDefault();

    const button = event.currentTarget;

    Rails.confirm(null, button, this._deleteConfirmData(button), {
      confirmCallback: () => {
        Rails.ajax({
          type: 'DELETE',
          url: button.dataset.deleteUrl,
          dataType: 'script',
          error: () => {
            this.element.dispatchEvent(
              new CustomEvent('ajax:error', { bubbles: true })
            );
          }
        });
      }
    });
  }

  deleteQuestionAlternative(event) {
    event.preventDefault();
    const button = event.currentTarget;
    Rails.confirm(null, button, button.dataset, {
      confirmCallback: () => {
        Rails.ajax({
          type: 'DELETE',
          url: button.dataset.deleteUrl,
          dataType: 'script',
          error: () => {
            this.element.dispatchEvent(new CustomEvent('ajax:error', { bubbles: true }));
          }
        });
      }
    });
  }

  selectCard(event) {
    event.stopPropagation();

    this.cardTargets.forEach(c => c.classList.remove('active'));

    event.currentTarget.classList.add('active');
  }

  onFormSubmit(event) {
    if (!this._validate()) {
      event.preventDefault();
      event.stopImmediatePropagation();

      this._scrollToFirstError();
    }
  }

  // The form pane scrolls on its own and is usually taller than the viewport,
  // so an error on an earlier card is invisible when the save is rejected
  _scrollToFirstError() {
    if (!this.hasFormTarget) {
      return;
    }

    const candidates = [
      ...this.formTarget.querySelectorAll('.input-form.error'),
      ...this.correctInputErrorTargets.filter(e => !e.classList.contains('hidden'))
    ];

    const error = candidates.sort((a, b) => (
      a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
    ))[0];

    if (!error) {
      return;
    }

    error.scrollIntoView({ behavior: 'smooth', block: 'center' });
    error.querySelector('input, textarea, select')?.focus({
      preventScroll: true
    });
  }

  _validate() {
    let isValid = true;

    this.correctInputErrorTargets.forEach(error => {
      const questionCard = error.closest('.js-question-card');
      const inputs = [
        ...this.correctRadioButtonTargets,
        ...this.correctCheckboxTargets
      ].filter(i => questionCard.contains(i));

      if (!inputs.some(i => i.checked)) {
        this._showCorrectAnswerMissingError(questionCard);
        isValid = false;
      } else {
        this._hideCorrectAnswerMissingError(questionCard);
      }
    });
    return isValid;
  }

  _onRadioChanged(event) {
    const questionCard = event.target.closest('.js-question-card');

    const allAnswersFromQuestionExcept = this.correctRadioButtonTargets
      .filter(input => input !== event.target && questionCard.contains(input));

    allAnswersFromQuestionExcept.forEach(input => { input.checked = false; });

    this._hideCorrectAnswerMissingError(questionCard);
  }

  _onCheckboxChanged(event) {
    const questionCard = event.target.closest('.js-question-card');

    const anyChecked = this.correctCheckboxTargets
      .filter(input => questionCard.contains(input))
      .some(input => input.checked);

    if (anyChecked) {
      this._hideCorrectAnswerMissingError(questionCard);
    } else {
      this._showCorrectAnswerMissingError(questionCard);
    }
  }

  _showCorrectAnswerMissingError(questionCard) {
    this.correctInputErrorTargets
      .filter(e => questionCard.contains(e))
      .forEach(e => e.classList.remove('hidden'));
  }

  _hideCorrectAnswerMissingError(questionCard) {
    this.correctInputErrorTargets
      .filter(e => questionCard.contains(e))
      .forEach(e => e.classList.add('hidden'));
  }

  _syncCorrectAnswer({ detail: { questionPartId, correctAlternativeIds } }) {
    const container = document
      .querySelector(`[data-alternative-container="${questionPartId}"]`);

    if (!container) {
      return;
    }

    container.querySelectorAll('[data-alternative-card]').forEach(card => {
      const altId = parseInt(card.dataset.alternativeCard, 10);
      const isCorrect = correctAlternativeIds.includes(altId);

      const input = card.querySelector("input[name$='[correct]'][value='true']");
      if (input) {
        input.checked = isCorrect;
      }

      const guard = card.querySelector('[data-delete-correct-guard]');
      const link  = card.querySelector('[data-delete-link]');

      if (guard) guard.classList.toggle('hidden', !isCorrect);
      if (link)  link.classList.toggle('hidden', isCorrect);
    });
  }

  _switchPageFromEvent(event) {
    const pageId = event.detail.id;

    this.pageTargets.forEach(page => {
        if (page.id.split('-').at(-1) === pageId) {
          page.classList.add('active')
        } else {
          page.classList.remove('active')
        }
      }
    );
  }
}
